/**
 * La rotta delle pagine su misura: `/portal/<qualcosa>` che non sia una pagina del prodotto.
 *
 * Sta in coda per costruzione — TanStack dà ai segmenti statici priorità su quelli dinamici,
 * quindi `/portal/commesse` continua a montare la sua pagina e questa raccoglie solo il resto.
 * Le sette pagine esistenti **tengono le loro URL**, che è un requisito e non un dettaglio:
 * sono nei preferiti dei clienti dei nostri clienti.
 *
 * Cosa fa questa rotta: legge dal `/me` la pagina assegnata a quel percorso, risolve la chiave
 * nel registro dei moduli e monta il componente. Tutto il resto — dati, stati, grafica — è del
 * modulo, che è una pagina normale scritta da noi su commessa.
 */
import { Suspense } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { fetchPortalMe } from '@/features/portal/clientPortalApi';
import { moduleFromPath } from '@/app/layouts/portalNav';
import { loadModule } from '@/features/portal/modules/registry';

export const Route = createFileRoute('/_portal/portal/$modulo')({ component: CustomPage });

/**
 * Il vicolo cieco, scritto per chi lo legge.
 *
 * Ci si arriva in due casi, entrambi temporanei e nessuno dei due colpa di chi guarda: un
 * indirizzo scritto a mano, oppure una pagina configurata dal server ma non ancora presente in
 * questa versione del portale (la finestra fra assegnazione e rilascio, o un rollback).
 *
 * Non diciamo «404 modulo non trovato»: davanti c'è il cliente di un nostro cliente, per cui
 * quella frase è rumore. La chiave finisce in un `data-` attribute, così se ci mandano uno
 * screenshot o ci guardiamo con gli strumenti da sviluppatore sappiamo subito cosa manca.
 */
function Unavailable({ itemKey: key }: { itemKey: string }) {
  return (
    <Center mih="60vh" data-modulo-mancante={key}>
      <Stack gap={6} align="center" maw={420} ta="center">
        <Title order={3} fz="var(--fs-h4)">Questa sezione non è disponibile</Title>
        <Text fz="sm" c="var(--ink-500)">
          Può essere un indirizzo non più valido, oppure una pagina in corso di attivazione.
          Se ti aspettavi di trovarla qui, riprova più tardi o scrivi al tuo referente.
        </Text>
      </Stack>
    </Center>
  );
}

function CustomPage() {
  const { modulo } = useParams({ from: '/_portal/portal/$modulo' });
  /* Stessa `queryKey` della shell: il `/me` è già in cache quando si arriva qui, quindi non
     c'è una seconda chiamata né uno sfarfallio di caricamento. */
  const meQ = useQuery({ queryKey: ['portal-me'], staleTime: 10 * 60_000, queryFn: fetchPortalMe });

  if (meQ.isLoading) return <Center mih="60vh"><Loader /></Center>;

  /* Ramo distinto, e non un dettaglio: senza, un `/me` che non risponde finirebbe nel vicolo
     cieco qui sotto — «questa sezione non è disponibile» — cioè un errore di rete travestito da
     pagina inesistente. Chi legge concluderebbe che la pagina non gli è stata data, e chiederebbe
     al fornitore sbagliato. */
  if (meQ.isError) {
    return (
      <Center mih="60vh">
        <Stack gap={10} align="center" maw={420} ta="center">
          <Title order={3} fz="var(--fs-h4)">Non riusciamo a caricare il portale</Title>
          <Text fz="sm" c="var(--ink-500)">
            La sezione esiste: è il collegamento al sistema che non risponde in questo momento.
            Riprova fra poco.
          </Text>
          <Button variant="light" onClick={() => void meQ.refetch()}>Riprova</Button>
        </Stack>
      </Center>
    );
  }

  const page = moduleFromPath(modulo, meQ.data?.customPages);
  if (!page) return <Unavailable itemKey={modulo} />;

  const Component = loadModule(page.key);
  if (!Component) return <Unavailable itemKey={page.key} />;

  return (
    /* Il modulo è un chunk a parte: fra il clic e il suo arrivo passa una richiesta di rete, e
       senza `Suspense` sarebbe una schermata bianca. */
    <Suspense fallback={<Center mih="60vh"><Loader /></Center>}>
      <Component
        config={page.config ?? {}}
        label={page.label}
        configVersion={page.configVersion ?? 1}
      />
    </Suspense>
  );
}
